// src/components/AuthRedirect.js

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../services/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { checkAgeRestriction } from "./onboardingCheck";
import logoImg from "../images/logo/v.png";
import { P1 } from "../components/theme/text/Headings";
import { Blue, Black, Purple } from "../components/theme/colors";
import { shouldRedirectToOnboarding } from "./onboardingCheck";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import styled from "styled-components";

const PageWrapper = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
`;

const Background = styled.div`
  position: absolute;
  background: linear-gradient(120deg, ${Purple}, ${Blue});
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  text-align: center;
`;

const LogoCircle = styled.div`
  width: 175px;
  height: 175px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${Black};
  margin-bottom: 20px;
`;

const AuthRedirect = () => {
  const { currentUserState, loading } = useAuth();
  const navigate = useNavigate();
  const [isUnder17, setIsUnder17] = useState(false);
  const [checkingAge, setCheckingAge] = useState(false);

  // Handle deep links
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handleDeepLink = ({ url }) => {
      console.log("🔥 AuthRedirect: Deep link detected:", url);

      // Ignore Firebase auth verification deep links - they shouldn't trigger navigation
      if (
        url.includes("firebaseauth/link") ||
        url.includes("authType=verifyApp")
      ) {
        console.log(
          "🚫 AuthRedirect: Ignoring Firebase auth verification deep link:",
          url
        );
        return;
      }

      try {
        // Parse URL to extract path and query
        const urlObj = new URL(url);
        let path = urlObj.pathname + urlObj.search;

        // Handle /s/ and /p/ prefixed routes (server-side meta routes)
        if (path.startsWith("/s/")) {
          const parts = path.split("?");
          const pathPart = parts[0];
          const query = parts[1] ? `?${parts[1]}` : "";
          const segments = pathPart.split("/").filter(Boolean);

          if (segments.length >= 2) {
            path = `/${segments[1]}${query}`;
          }
        } else if (path.startsWith("/p/")) {
          path = path.replace("/p/", "/");
        }

        setTimeout(() => {
          console.log("📍 AuthRedirect: Navigating to deep link:", path);
          navigate(path);
        }, 1000);
      } catch (err) {
        console.warn("❌ AuthRedirect: Could not parse deep link:", url);
      }
    };

    CapacitorApp.addListener("appUrlOpen", handleDeepLink);

    CapacitorApp.getLaunchUrl().then((res) => {
      if (res?.url) {
        console.log("🚀 AuthRedirect: Cold start URL:", res.url);
        handleDeepLink(res);
      }
    });

    return () => {
      CapacitorApp.removeAllListeners();
    };
  }, [navigate]);

  // Reset deep link state on component mount to prevent stuck states
  useEffect(() => {
    // Reset any stuck deep link flags from other components
    if (window.resetDeepLinkState) {
      window.resetDeepLinkState();
    }
  }, []);

  // Check age restriction when user state changes
  useEffect(() => {
    const checkAgeRestrictionStatus = async () => {
      if (!currentUserState || loading) {
        setIsUnder17(false);
        setCheckingAge(false);
        return;
      }

      setCheckingAge(true);
      try {
        const isAgeRestricted = await checkAgeRestriction(currentUserState);
        setIsUnder17(isAgeRestricted);
      } catch (error) {
        console.error("Error checking age restriction:", error);
        setIsUnder17(false);
      } finally {
        setCheckingAge(false);
      }
    };

    checkAgeRestrictionStatus();
  }, [currentUserState, loading]);

  // Main redirect logic
  useEffect(() => {
    if (!loading && !checkingAge) {
      console.log("AuthRedirect: currentUserState:", currentUserState);

      const performRedirect = async () => {
        try {
          if (currentUserState) {
            // Check age restriction first
            if (isUnder17) {
              console.log("User is under 17, redirecting to age restriction");
              navigate("/age-restriction");
              return;
            }

            // Check if user needs to complete onboarding
            const needsOnboarding = await shouldRedirectToOnboarding(
              currentUserState
            );
            if (needsOnboarding) {
              // Platform-specific redirect: Web users go to download, Native users go to onboarding
              const isWebPlatform = !Capacitor.isNativePlatform();
              if (isWebPlatform) {
                console.log(
                  "Web user needs to be on native, redirecting to /download"
                );
                navigate("/download");
              } else {
                console.log(
                  "Native user needs onboarding, redirecting to /onboarding-start"
                );
                navigate("/onboarding-start");
              }
            } else {
              console.log(
                "User has completed onboarding, redirecting to /home"
              );
              navigate("/home");
            }
          } else {
            console.log("User is NOT logged in. Redirecting to /login");
            navigate("/login");
          }
        } catch (error) {
          console.error("Error in redirect logic:", error);
          // Fallback to login if there's an error
          navigate("/login");
        }
      };

      // Add a small delay to ensure everything is properly initialized
      setTimeout(performRedirect, 1000);
    }
  }, [loading, currentUserState, navigate, isUnder17, checkingAge]);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="splash"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.6 }}
        style={{ width: "100%", height: "100vh" }}
      >
        <PageWrapper>
          <Background />
          <ContentWrapper>
            {/* Logo with black circle background */}
            <LogoCircle>
              <img
                src={logoImg}
                alt="Vibe Check Logo"
                style={{ height: "150px" }}
              />
            </LogoCircle>

            {/* Bottom text */}
            <P1
              style={{
                position: "absolute",
                bottom: "100px",
                left: "50%",
                transform: "translateX(-50%)",
                color: Black,
              }}
            >
              Made by Clickbait Labs, Inc.
            </P1>
          </ContentWrapper>
        </PageWrapper>
      </motion.div>
    </AnimatePresence>
  );
};

export default AuthRedirect;
